import { useEffect, useState } from 'react'
import { Label, ListBox, ListBoxItem, SearchField, Select } from '@heroui/react'

import type {
  ProductBrand,
  ProductCategory,
  ProductEnabledFilter,
  ProductFilterValues,
  ProductStatusFilter,
} from '../types'
import { ProductEnabledFilter as Enabled, ProductStatusFilter as Status, translated } from '../types'

/** A key React Aria can hold that stands for "no filter" — `null` clears the trigger. */
const ANY = 'any'

interface ProductFiltersProps {
  value: ProductFilterValues
  onChange: (value: ProductFilterValues) => void
  categories: ProductCategory[]
  /** Every brand, inactive ones included — a product can still carry one. */
  brands: ProductBrand[]
  isDisabled?: boolean
}

/** One dropdown, without a form around it. */
function FilterSelect({
  label,
  selected,
  options,
  onSelect,
  isDisabled,
}: {
  label: string
  selected: string | null
  options: { value: string; label: string }[]
  onSelect: (value: string | null) => void
  isDisabled?: boolean
}) {
  return (
    <Select
      selectedKey={selected ?? ANY}
      onSelectionChange={(key) => onSelect(key === ANY ? null : String(key))}
      isDisabled={isDisabled}
      className="flex w-full flex-col gap-1.5 sm:w-44"
    >
      <Label className="text-muted text-xs">{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {[{ value: ANY, label: `All ${label.toLowerCase()}` }, ...options].map((option) => (
            <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
              {option.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/** How long the merchant has to stop typing before the list is asked again. */
const SEARCH_DEBOUNCE_MS = 300

export function ProductFilters({
  value,
  onChange,
  categories,
  brands,
  isDisabled,
}: ProductFiltersProps) {
  // The box is typed into far faster than the list can answer, so it holds its own
  // value and only pushes one up once the typing settles.
  const [search, setSearch] = useState(value.searchText)
  const [lastPushed, setLastPushed] = useState(value.searchText)

  // A filter cleared elsewhere — the empty state's reset — has to reach the box too.
  // Adjusting during render rather than in an effect keeps it to a single pass.
  if (value.searchText !== lastPushed) {
    setLastPushed(value.searchText)
    setSearch(value.searchText)
  }

  useEffect(() => {
    if (search === value.searchText) return

    const timer = setTimeout(() => onChange({ ...value, searchText: search }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search, value, onChange])

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <SearchField
        value={search}
        onChange={setSearch}
        isDisabled={isDisabled}
        aria-label="Search products"
        className="flex flex-1 flex-col gap-1.5"
      >
        <Label className="text-muted text-xs">Search</Label>
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder="Name or SKU" />
          <SearchField.ClearButton />
        </SearchField.Group>
      </SearchField>

      <FilterSelect
        label="Categories"
        selected={value.categoryId}
        isDisabled={isDisabled}
        options={categories.map((category) => ({
          value: category.id,
          label: translated(category.name),
        }))}
        onSelect={(categoryId) => onChange({ ...value, categoryId })}
      />

      <FilterSelect
        label="Brands"
        selected={value.brandId}
        isDisabled={isDisabled}
        options={brands.map((brand) => ({
          value: brand.id,
          label:
            brand.isActive === false
              ? `${translated(brand.name)} (inactive)`
              : translated(brand.name),
        }))}
        onSelect={(brandId) => onChange({ ...value, brandId })}
      />

      <FilterSelect
        label="Status"
        selected={value.status}
        isDisabled={isDisabled}
        options={[
          { value: Status.Published, label: 'Published' },
          { value: Status.Unpublished, label: 'Unpublished' },
        ]}
        onSelect={(status) => onChange({ ...value, status: status as ProductStatusFilter | null })}
      />

      <FilterSelect
        label="Availability"
        selected={value.enabled}
        isDisabled={isDisabled}
        options={[
          { value: Enabled.Enabled, label: 'Enabled' },
          { value: Enabled.Disabled, label: 'Disabled' },
        ]}
        onSelect={(enabled) =>
          onChange({ ...value, enabled: enabled as ProductEnabledFilter | null })
        }
      />
    </div>
  )
}
